import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import type {
  ApiResponse,
  AchievementSummary,
  RecentBadge,
  UnnotifiedBadge,
} from "@tacklefour/api-types";

export function useAchievements() {
  const api = useApiClient();
  return useQuery({
    queryKey: ["achievements"],
    queryFn: () => api<ApiResponse<AchievementSummary>>("/badges"),
    staleTime: 2 * 60 * 1000,
    select: (res) => res.data,
  });
}

export function useRecentBadges() {
  const api = useApiClient();
  return useQuery({
    queryKey: ["achievements", "recent"],
    queryFn: () => api<ApiResponse<RecentBadge[]>>("/badges/recent"),
    staleTime: 2 * 60 * 1000,
    select: (res) => res.data,
  });
}

export function useUnnotifiedBadges() {
  const api = useApiClient();
  return useQuery({
    queryKey: ["achievements", "unnotified"],
    queryFn: () => api<ApiResponse<UnnotifiedBadge[]>>("/badges/unnotified"),
    refetchInterval: 60 * 1000, // Poll every 60s
    select: (res) => res.data,
  });
}

export function useMarkBadgeNotified() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (badgeId: string) =>
      api(`/badges/${badgeId}/notified`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achievements", "unnotified"] });
    },
  });
}

export function useTrackShare() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api("/badges/track-share", { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achievements"] });
    },
  });
}
